import { put, takeLatest, call } from 'redux-saga/effects';
import {
  GET_LIBRARY_WORKOUTS,
  CREATE_WORKOUT,
  UPDATE_LIBRARY_WORKOUT,
  GET_USER_WORKOUTS,
  DELETE_LIBRARY_WORKOUT,
  CREATE_USER_GENERATED_WORKOUT,
  SHARE_MY_WORKOUT,
} from './constants';
import {
  getLibraryWorkoutsSuccess,
  getLibraryWorkoutsError,
  createWorkoutSuccess,
  createWorkoutError,
  updateLibraryWorkoutSuccess,
  updateLibraryWorkoutError,
  deleteLibraryWorkoutSuccess,
  getUserWorkoutsSuccess,
  getUserWorkoutsError,
  createUserGeneratedWorkoutSuccess,
  createUserGeneratedWorkoutError,
  shareMyWorkoutSuccess,
  shareMyWorkoutError,
} from './actions';
import { fetchData } from '../api';

export function* getLibraryWorkouts({
  page = 1,
  limit = 50,
  search = '',
  draft = null,
  filters = {},
}) {
  try {
    // Build query string with pagination and filter parameters
    let url = `${process.env.REACT_APP_API_URL}/api/workout/library?page=${page}&limit=${limit}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (draft !== null) {
      url += `&draft=${draft}`;
    }
    // Discovery-tag filters. Empty values + empty arrays are skipped
    // so the URL stays clean when the trainer hasn't picked anything
    // (matches the backend's `if (req.query.goal)` style guards).
    if (filters.goal) {
      url += `&goal=${encodeURIComponent(filters.goal)}`;
    }
    if (filters.level) {
      url += `&level=${encodeURIComponent(filters.level)}`;
    }
    if (Array.isArray(filters.bodyFocus) && filters.bodyFocus.length > 0) {
      url += `&bodyFocus=${encodeURIComponent(filters.bodyFocus.join(','))}`;
    }
    if (Array.isArray(filters.equipment) && filters.equipment.length > 0) {
      url += `&equipment=${encodeURIComponent(filters.equipment.join(','))}`;
    }
    // Unfinished client sessions saved as drafts. The API leaves them out
    // unless asked; only the library page asks (and labels them).
    if (filters.includeClientDrafts) {
      url += '&includeClientDrafts=true';
    }

    const token = window.localStorage.token;
    const config = {
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
    };

    let response = yield call(fetchData, url, config);

    // Handle both old format (array) and new format (object with workouts and pagination)
    // This provides backward compatibility during migration
    if (Array.isArray(response)) {
      // Old format: array of workouts
      yield put(
        getLibraryWorkoutsSuccess({
          workouts: response,
          pagination: {
            page: 1,
            limit: response.length,
            total: response.length,
            pages: 1,
            hasNext: false,
            hasPrev: false,
          },
        })
      );
    } else {
      // New format: object with workouts and pagination
      yield put(getLibraryWorkoutsSuccess(response));
    }
  } catch (err) {
    // The original catch called `err.json()` unconditionally, which
    // only works when `err` is a fetch `Response`. Network errors,
    // CORS preflight failures, or backend responses with non-JSON
    // bodies (HTML error page from a proxy, empty 500, etc.) would
    // make the parse throw — and that throw escaped the catch,
    // killing the saga before it could dispatch an ERROR action.
    // The reducer's `getWorkoutsLoading: false` transition never
    // fired, leaving the skeleton spinning forever for any user
    // whose first /library load hit any of these paths.
    //
    // Always dispatch the ERROR action so the loading flag clears,
    // regardless of what was thrown. Best-effort parse for the
    // payload.
    console.error('getLibraryWorkouts failed:', err);
    let errorPayload = null;
    if (err && typeof err.json === 'function') {
      try {
        errorPayload = yield err.json();
      } catch (_) {
        // Body wasn't JSON — fall through with status info.
        errorPayload = {
          status: err.status,
          message: err.statusText || 'Unknown error',
        };
      }
    } else {
      errorPayload = { message: err?.message || String(err) };
    }
    yield put(getLibraryWorkoutsError(errorPayload));
  }
}

export function* createWorkout({ data }) {
  const token = localStorage.token;
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/workout`;
    const config = {
      method: 'POST',
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };
    const workout = yield call(fetchData, url, config);
    yield put(createWorkoutSuccess(workout));
  } catch (err) {
    console.log(err);
    let error;
    try {
      // Check if err has json method (Response object)
      if (err && typeof err.json === 'function') {
        error = yield err.json();
      } else {
        // Handle network errors or other error types
        error = err.message || err.toString() || err;
      }
    } catch (parseError) {
      error = err.message || err.toString() || 'Unknown error occurred';
    }
    console.log(error);
    yield put(createWorkoutError(error));
  }
}

export function* updateLibraryWorkout({ workout }) {
  const token = localStorage.token;
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/workout/library/${workout._id}`;
    const config = {
      method: 'PUT',
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(workout),
    };

    const createdExercise = yield call(fetchData, url, config);
    yield put(updateLibraryWorkoutSuccess(createdExercise));
  } catch (err) {
    console.log(err);
    const error = yield err.json();
    console.log(error);
    yield put(updateLibraryWorkoutError(error));
  }
}

export function* deleteLibraryWorkout({ id }) {
  const token = localStorage.token;
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/workout/library/${id}`;
    const config = {
      method: 'DELETE',
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
    };
    yield call(fetchData, url, config);
    yield put(deleteLibraryWorkoutSuccess(id));
  } catch (err) {
    console.log(err);
    const error = yield err.json();
    console.log(error);
  }
}

export function* getUserWorkouts({ clientId, currentWeek, averageMonth, page, limit }) {
  try {
    let url = `${process.env.REACT_APP_API_URL}/api/workout/user/${clientId}`;
    if (currentWeek && averageMonth) {
      url += '?currentWeek=true&averageMonth=true';
    } else {
      const pageNum = page != null ? page : 1;
      const limitNum = limit != null ? limit : 50;
      url += `?page=${pageNum}&limit=${limitNum}`;
    }

    const token = window.localStorage.token;
    const config = {
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
    };

    const response = yield call(fetchData, url, config);
    yield put(getUserWorkoutsSuccess(response));
  } catch (err) {
    console.log(err);
    const error = yield err.json();
    console.log(error);
    yield put(getUserWorkoutsError(error));
  }
}

export function* createUserGeneratedWorkout({ data }) {
  const token = localStorage.token;
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/workout/user-generated-workout/${data.client}`;
    const config = {
      method: 'POST',
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };
    const userWorkout = yield call(fetchData, url, config);
    yield put(createUserGeneratedWorkoutSuccess(userWorkout));
  } catch (err) {
    console.log(err);
    // Guard the parse (mirrors createWorkout above). A gateway/proxy failure
    // (Heroku H12, 502/503) returns a non-JSON body, so err.json() would reject
    // — and an unguarded reject here would skip the ERROR dispatch entirely,
    // leaving createWorkoutLoading stuck true and the failed-log toast silent.
    let error;
    try {
      if (err && typeof err.json === 'function') {
        error = yield err.json();
      } else {
        error = err && (err.message || err.toString());
      }
    } catch (parseError) {
      error = { message: (err && err.statusText) || 'Save failed' };
    }
    console.log(error);
    yield put(createUserGeneratedWorkoutError(error));
  }
}

export function* shareMyWorkout({ workoutToken }) {
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/workout/share/${workoutToken}`;
    let workoutToShare = yield call(fetchData, url);
    yield put(shareMyWorkoutSuccess(workoutToShare));
  } catch (err) {
    console.log(err);
    const error = yield err.json();
    console.log(error);
    yield put(shareMyWorkoutError(error));
  }
}

export default function* workoutsSaga() {
  yield takeLatest(GET_LIBRARY_WORKOUTS, getLibraryWorkouts);
  yield takeLatest(CREATE_WORKOUT, createWorkout);
  yield takeLatest(UPDATE_LIBRARY_WORKOUT, updateLibraryWorkout);
  yield takeLatest(DELETE_LIBRARY_WORKOUT, deleteLibraryWorkout);
  yield takeLatest(GET_USER_WORKOUTS, getUserWorkouts);
  yield takeLatest(CREATE_USER_GENERATED_WORKOUT, createUserGeneratedWorkout);
  yield takeLatest(SHARE_MY_WORKOUT, shareMyWorkout);
}
